import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CheckCircle, Shield, Award, Star } from 'lucide-react';

// Container for whole section
const BannerContainer = styled.div`
  width: 95%;
  padding: 30px 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
  display: flex;
  justify-content: center;
  border-radius: 24px;
  border: 1px solid #d7e6f8;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
`;

// Grid wrapper for all 4 items
const ItemsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px 40px;
  max-width: 1050px;
  justify-content: center;
`;

// Single item styling
const StyledBannerItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  width: 46%;
  min-width: 280px;
  opacity: 0;
  transform: translateX(${({ direction }) => (direction === 'left' ? '-30px' : '30px')});
  transition: all 0.25s ease;

  ${({ inView }) =>
    inView &&
    css`
      opacity: 1;
      transform: translateX(0);
    `}
`;

// Icon wrapper
const IconWrapper = styled.div`
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 50%;
  padding: 12px;
  min-width: 52px;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Text groups
const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.p`
  font-size: 1.2rem;
  font-weight: 700;
  color: #0b3d73;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Subtitle = styled.span`
  font-size: 0.95rem;
  color: #52657a;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

// Observer logic
function BannerItem({ icon, title, subtitle, direction }) {
  const ref = useRef();
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <StyledBannerItem ref={ref} inView={inView} direction={direction}>
      <IconWrapper>{icon}</IconWrapper>
      <TextGroup>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </TextGroup>
    </StyledBannerItem>
  );
}

// Main component
export default function Banner() {
  const items = [
    {
      icon: <CheckCircle size={28} color="#1890ff" />,
      title: 'Verified Tradespeople',
      subtitle: 'Every professional is pre-screened and vetted.',
    },
    {
      icon: <Shield size={28} color="#1890ff" />,
      title: 'Insured for Any Outcome',
      subtitle: 'Trust that everything is covered.',
    },
    {
      icon: <Award size={28} color="#1890ff" />,
      title: 'Fully Qualified Professionals',
      subtitle: 'All trades meet industry certification standards.',
    },
    {
      icon: <Star size={28} color="#1890ff" />,
      title: 'Proven Track Records',
      subtitle: 'Rated by real customers in your area.',
    },
  ];

  return (
    <BannerContainer>
      <ItemsGrid>
        {items.map((item, i) => (
          <BannerItem key={i} {...item} direction={i % 2 === 0 ? 'left' : 'right'} />
        ))}
      </ItemsGrid>
    </BannerContainer>
  );
}
